import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импортируем компоненты калькулятора
import ConverterForm from './CurrencyConverterPage/components/ConverterForm';
import ConversionResults from './CurrencyConverterPage/components/ConversionResults';
import RateTable from './CurrencyConverterPage/components/RateTable';
import CurrencyChart from './CurrencyConverterPage/components/CurrencyChart';
import SavedConversions from './CurrencyConverterPage/components/SavedConversions';
import InfoContent from './CurrencyConverterPage/components/InfoContent';

/**
 * Компонент CurrencyConverterPage - продвинутый конвертер валют с API, историческими данными и графиками
 */
const CurrencyConverterPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();
  
  // Получаем данные инструмента
  const tool = getToolById('currency-converter');
  
  // Состояние для валют и курсов
  const [currencies, setCurrencies] = useState({
    USD: { code: 'USD', name: 'Доллар США', symbol: '$' },
    EUR: { code: 'EUR', name: 'Евро', symbol: '€' },
    RUB: { code: 'RUB', name: 'Российский рубль', symbol: '₽' },
    GBP: { code: 'GBP', name: 'Фунт стерлингов', symbol: '£' },
    JPY: { code: 'JPY', name: 'Японская иена', symbol: '¥' },
    CNY: { code: 'CNY', name: 'Китайский юань', symbol: '¥' },
    AUD: { code: 'AUD', name: 'Австралийский доллар', symbol: 'A$' },
    CAD: { code: 'CAD', name: 'Канадский доллар', symbol: 'C$' },
    CHF: { code: 'CHF', name: 'Швейцарский франк', symbol: 'Fr' },
    INR: { code: 'INR', name: 'Индийская рупия', symbol: '₹' },
    // Дополнительные валюты
    HKD: { code: 'HKD', name: 'Гонконгский доллар', symbol: 'HK$' },
    SGD: { code: 'SGD', name: 'Сингапурский доллар', symbol: 'S$' },
    KRW: { code: 'KRW', name: 'Южнокорейская вона', symbol: '₩' },
    TRY: { code: 'TRY', name: 'Турецкая лира', symbol: '₺' },
    BRL: { code: 'BRL', name: 'Бразильский реал', symbol: 'R$' },
    MXN: { code: 'MXN', name: 'Мексиканское песо', symbol: 'Mex$' },
    ZAR: { code: 'ZAR', name: 'Южноафриканский рэнд', symbol: 'R' },
    AED: { code: 'AED', name: 'Дирхам ОАЭ', symbol: 'د.إ' },
    PLN: { code: 'PLN', name: 'Польский злотый', symbol: 'zł' },
    THB: { code: 'THB', name: 'Тайский бат', symbol: '฿' }
  });
  
  // Состояние для курсов валют
  const [exchangeRates, setExchangeRates] = useState({});
  
  // Состояние для последнего времени обновления курсов
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Состояние загрузки
  const [loading, setLoading] = useState(true);
  
  // Состояние ошибки API
  const [apiError, setApiError] = useState(null);
  
  // Состояние формы
  const [formData, setFormData] = useState({
    amount: '1',
    fromCurrency: 'USD',
    toCurrency: 'RUB',
  });
  
  // Состояние результатов
  const [results, setResults] = useState(null);
  
  // Состояние сохраненных конвертаций (из localStorage)
  const [savedConversions, setSavedConversions] = useState([]);
  
  // Состояние для исторических данных (для графика)
  const [historicalData, setHistoricalData] = useState(null);
  
  // Состояние загрузки исторических данных
  const [loadingHistorical, setLoadingHistorical] = useState(false);
  
  // Функция для загрузки актуальных курсов валют через API
  const fetchExchangeRates = async () => {
    setLoading(true);
    setApiError(null);
    
    try {
      // Используем бесплатное API для получения курсов валют
      // exchangerate-api.com предоставляет бесплатный доступ с лимитом запросов
      const response = await fetch('https://open.er-api.com/v6/latest/USD');
      const data = await response.json();
      
      if (data && data.rates) {
        setExchangeRates(data.rates);
        setLastUpdated(new Date(data.time_last_update_utc));
      } else {
        throw new Error('Invalid API response format');
      }
    } catch (error) {
      console.error("Error fetching exchange rates:", error);
      setApiError("Не удалось загрузить текущие курсы валют. Используются резервные курсы.");
      
      // Используем резервные курсы на случай проблем с API
      setExchangeRates({
        USD: 1.0,
        EUR: 0.92,
        RUB: 92.5,
        GBP: 0.79,
        JPY: 150.7,
        CNY: 7.23,
        AUD: 1.52,
        CAD: 1.36,
        CHF: 0.90,
        INR: 83.4,
        HKD: 7.82,
        SGD: 1.35,
        KRW: 1350.2,
        TRY: 30.5,
        BRL: 5.2,
        MXN: 17.8,
        ZAR: 18.6,
        AED: 3.67,
        PLN: 4.0,
        THB: 35.5
      });
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  };
  
  // Функция для загрузки исторических данных (для графика)
  const fetchHistoricalData = async (fromCurrency, toCurrency) => {
    setLoadingHistorical(true);
    
    try {
      // В бесплатном API мы не можем получить исторические данные
      // Для демонстрации создаем синтетические данные
      const today = new Date();
      const data = [];
      
      // Генерируем данные за последние 30 дней
      // В реальном приложении здесь будет запрос к API с историческими данными
      for (let i = 30; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        
        // Базовый курс +/- случайное отклонение до 5%
        const baseRate = exchangeRates[toCurrency] / exchangeRates[fromCurrency];
        const fluctuation = 1 + (Math.random() * 0.1 - 0.05);
        
        data.push({
          date: date.toISOString().split('T')[0],
          rate: baseRate * fluctuation
        });
      }
      
      setHistoricalData({
        fromCurrency,
        toCurrency,
        data
      });
    } catch (error) {
      console.error("Error fetching historical data:", error);
    } finally {
      setLoadingHistorical(false);
    }
  };
  
  // Функция конвертации валюты
  const convertCurrency = (amount, fromCurrency, toCurrency, rates) => {
    // Получаем курсы относительно USD (базовая валюта в API)
    const fromRate = rates[fromCurrency] || 1;
    const toRate = rates[toCurrency] || 1;
    
    // Конвертируем через общую базовую валюту (USD)
    return (amount / fromRate) * toRate;
  };
  
  // Функция расчета конвертации
  const calculateConversion = () => {
    const amount = parseFloat(formData.amount) || 0;
    const { fromCurrency, toCurrency } = formData;
    
    if (amount <= 0 || !fromCurrency || !toCurrency) {
      return;
    }
    
    const convertedAmount = convertCurrency(
      amount,
      fromCurrency,
      toCurrency,
      exchangeRates
    );
    
    const exchangeRate = convertCurrency(
      1,
      fromCurrency,
      toCurrency,
      exchangeRates
    );
    
    const reverseRate = convertCurrency(
      1,
      toCurrency,
      fromCurrency,
      exchangeRates
    );
    
    setResults({
      originalAmount: amount,
      originalCurrency: fromCurrency,
      convertedAmount,
      targetCurrency: toCurrency,
      exchangeRate,
      reverseRate,
      date: new Date()
    });
    
    // Загружаем исторические данные при изменении валютной пары
    fetchHistoricalData(fromCurrency, toCurrency);
  };
  
  // Функция сохранения конвертации
  const saveConversion = () => {
    if (!results) return;
    
    const newSavedConversion = {
      id: Date.now().toString(),
      fromAmount: results.originalAmount,
      fromCurrency: results.originalCurrency,
      toAmount: results.convertedAmount,
      toCurrency: results.targetCurrency,
      rate: results.exchangeRate,
      date: new Date().toISOString()
    };
    
    const updatedSavedConversions = [...savedConversions, newSavedConversion];
    setSavedConversions(updatedSavedConversions);
    
    // Сохраняем в localStorage
    localStorage.setItem('savedConversions', JSON.stringify(updatedSavedConversions));
  };
  
  // Функция удаления сохраненной конвертации
  const deleteConversion = (id) => {
    const updatedSavedConversions = savedConversions.filter(conv => conv.id !== id);
    setSavedConversions(updatedSavedConversions);
    
    // Обновляем localStorage
    localStorage.setItem('savedConversions', JSON.stringify(updatedSavedConversions));
  };
  
  // Инициализация - загрузка данных при монтировании компонента
  useEffect(() => {
    // Устанавливаем заголовок страницы
    document.title = tool?.metaTitle || 'Конвертер валют | Gensio';
    
    // Устанавливаем мета-описание
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && tool?.metaDescription) {
      metaDescription.setAttribute('content', tool.metaDescription);
    }
    
    // Загружаем курсы валют
    fetchExchangeRates();
    
    // Загружаем сохраненные конвертации из localStorage
    const storedConversions = localStorage.getItem('savedConversions');
    if (storedConversions) {
      try {
        setSavedConversions(JSON.parse(storedConversions));
      } catch (e) {
        console.error("Error parsing saved conversions:", e);
      }
    }
    
    // Проверяем наличие предустановленной конвертации из URL
    if (subToolId) {
      const currencyPair = subToolId.split('-');
      if (currencyPair.length === 2) {
        const fromCurrency = currencyPair[0].toUpperCase();
        const toCurrency = currencyPair[1].toUpperCase();
        
        if (currencies[fromCurrency] && currencies[toCurrency]) {
          setFormData(prev => ({
            ...prev,
            fromCurrency,
            toCurrency
          }));
        }
      }
    }
  }, [tool, subToolId]);
  
  // Эффект для автоматического расчета при изменении курсов или формы
  useEffect(() => {
    if (Object.keys(exchangeRates).length > 0) {
      calculateConversion();
    }
  }, [exchangeRates, formData]);
  
  // Эффект для периодического обновления курсов (каждые 30 минут)
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchExchangeRates();
    }, 30 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
      {/* Фоновый узор */}
      <div className="fixed inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)] [background-size:20px_20px] opacity-25 pointer-events-none"></div>
      
      {/* Навигационная панель */}
      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      
      {/* Основное содержимое */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Хлебные крошки */}
        <Breadcrumbs />
        
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Основной контент */}
          <div className="lg:w-2/3">
            {/* Заголовок и описание */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {tool?.name || 'Конвертер валют'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {tool?.description || 'Мгновенный перевод между любыми валютами мира с актуальным курсом и исторической аналитикой.'}
              </p>
              
              {/* Информация о последнем обновлении курсов */}
              <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                <span>
                  Курсы обновлены: {lastUpdated ? new Date(lastUpdated).toLocaleString('ru-RU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'загрузка...'}
                </span>
              </div>
            </div>
            
            {/* Предупреждение при ошибке API */}
            {apiError && (
              <div className="mb-6 p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 flex items-start">
                <AlertTriangle className="flex-shrink-0 h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-3 mt-0.5" />
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  {apiError}
                </p>
              </div>
            )}
            
            {/* Форма конвертера */}
            <ConverterForm 
              formData={formData}
              setFormData={setFormData}
              currencies={currencies}
              loading={loading}
              calculateConversion={calculateConversion}
              saveConversion={saveConversion}
              results={results}
            />
            
            {/* Результаты конвертации */}
            {results && (
              <ConversionResults 
                results={results}
                currencies={currencies}
                saveConversion={saveConversion}
              />
            )}
            
            {/* График курса валют */}
            {historicalData && (
              <CurrencyChart 
                historicalData={historicalData}
                currencies={currencies}
                loading={loadingHistorical}
              />
            )}
            
            {/* Таблица основных курсов валют */}
            <RateTable 
              exchangeRates={exchangeRates}
              currencies={currencies}
              baseCurrency={formData.fromCurrency}
              loading={loading}
            />
            
            {/* Сохраненные конвертации */}
            {savedConversions.length > 0 && (
              <SavedConversions 
                savedConversions={savedConversions}
                currencies={currencies}
                deleteConversion={deleteConversion}
                setFormData={setFormData}
              />
            )}
            
            {/* Информация о конвертере */}
            <InfoContent />
          </div>
          
          {/* Боковая панель */}
          <div className="lg:w-1/3">
            <ToolSidebar 
              currentToolId="currency-converter"
              currentSubToolId={subToolId}
              tool={tool}
            />
          </div>
        </div>
      </main>
      
      {/* Футер */}
      <Footer />
    </div>
  );
};

export default CurrencyConverterPage;